import {
  ArrowDownwardRounded,
  CheckCircleRounded,
  KeyboardArrowDown,
  KeyboardArrowUp,
  OpenInNewRounded,
  RadioButtonUncheckedRounded,
  UnpublishedRounded,
} from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ThemeProvider,
  Typography,
  styled,
  useTheme,
} from '@mui/material';
import { APP_DATE_FORMAT } from 'app/config/constants';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { InspectionImageInput } from 'app/modules/dashboards/dashboard-truck-implement/components/truck-implement-inspection-tab/InspectionImageInput';
import { NoData } from 'app/shared/components/no-data/NoData';
import { PageFilters } from 'app/shared/components/page-filters/PageFilters';
import { PageHeader } from 'app/shared/components/page-header/PageHeader';
import { PaginationComponent } from 'app/shared/components/pagination/Pagination';
import { ISearchTypes } from 'app/shared/interfaces/searchTypesInterface';
import { FormControlDefault } from 'app/shared/layout/register/DefaultFormsComponents';
import { InspectionStatus } from 'app/shared/model/enumerations/inspection-status.model';
import { InspectionTypes } from 'app/shared/model/enumerations/inspection-types.model';
import { IInspectionHistory } from 'app/shared/model/inspection-history.model';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { Loading } from 'app/shared/util/loading';
import { ASC, DEFAULT_ITEMS_PER_PAGE, DESC } from 'app/shared/util/pagination.constants';
import React, { useCallback, useEffect, useState } from 'react';
import { TextFormat, Translate, getSortState, translate } from 'react-jhipster';
import { Link, RouteComponentProps } from 'react-router-dom';
import { getAllInspectionItemPerformedsByHistoryId } from '../inspection-item-performed/inspection-item-performed.reducer';
import { getEntities } from './inspection-history.reducer';

interface CustomTableRowProps {
  key: number;
  inspectionHistory: IInspectionHistory;
}

const headers = [
  { key: 'i.name', component: 'fleetSenseApp.inspectionHistory.name', sortable: true },
  { key: 'ti.license_plate', component: 'fleetSenseApp.inspectionHistory.truckImplement', sortable: true },
  { key: 'au.first_name', component: 'fleetSenseApp.inspectionHistory.user', sortable: true },
  { key: 'created', component: 'fleetSenseApp.inspectionHistory.createdAt', sortable: true },
  { key: 'status', component: 'fleetSenseApp.inspectionHistory.status', sortable: true },
];

export const InspectionHistory = (props: RouteComponentProps<{ url: string }>) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const urlParams = new URLSearchParams(props.location.search);

  const loadingInspectionItemPerformeds = useAppSelector(state => state.inspectionItemPerformed.loading);
  const inspectionItemPerformeds = useAppSelector(state => state.inspectionItemPerformed.entities);
  const inspectionHistoryList = useAppSelector(state => state.inspectionHistory.entities);
  const totalItems = useAppSelector(state => state.inspectionHistory.totalItems);
  const loading = useAppSelector(state => state.inspectionHistory.loading);

  const { match } = props;

  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [nameSearch, setNameSearch] = useState('');
  const [inspectionHistoryId, setInspectionHistoryId] = useState(urlParams.get('inspectionHistoryId') || '');

  const [open, setOpen] = useState(false);

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(
      {
        ...getSortState(props.location, DEFAULT_ITEMS_PER_PAGE, 'created'),
        order: DESC,
      },
      props.location.search
    )
  );

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  const getAllEntities = useCallback(() => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
        nameSearch,
        inspectionHistoryId: inspectionHistoryId ? +inspectionHistoryId : null,
      })
    );
  }, [paginationState]);

  useEffect(() => {
    getAllEntities();
  }, [getAllEntities]);

  const sortEntities = () => {
    const searchParams = [];
    if (nameSearch !== '') {
      searchParams.push(`nameSearch=${encodeURIComponent(nameSearch)}`);
    }
    if (inspectionHistoryId !== '') {
      searchParams.push(`inspectionHistoryId=${encodeURIComponent(inspectionHistoryId)}`);
    }

    const searchQueryString = searchParams.length > 0 ? `&${searchParams.join('&')}` : '';

    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}${searchQueryString}`;

    if (props.location.search !== endURL) {
      props.history.replace(`${props.location.pathname}${endURL}`);
    }
  };

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const getinspectionItemPerformeds = ({ id }: IInspectionHistory) => {
    dispatch(getAllInspectionItemPerformedsByHistoryId(id));
  };

  const setActivePage = () => {
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
  };

  const setNameSearchPageHeader = () => {
    if (inspectionHistoryId && inspectionHistoryList?.length) {
      return inspectionHistoryList?.find(inspection => inspection.id === +inspectionHistoryId)?.inspection.name;
    }
    return nameSearch;
  };

  const statusChip = ({ status }: IInspectionHistory) => (
    <Chip
      sx={{ width: '115px' }}
      label={status ? <Translate contentKey={`fleetSenseApp.InspectionStatus.${status}`} /> : ''}
      color={status === InspectionStatus.MAINTENANCE ? 'warning' : status === InspectionStatus.COMPLIANT ? 'success' : 'error'}
    />
  );

  const summaryDetails = ({ inspection, observations }: IInspectionHistory) => {
    return loadingInspectionItemPerformeds ? (
      <Loading />
    ) : inspection?.type === InspectionTypes.DAILY ? (
      <TableContainer sx={{ overflowY: 'hidden' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <CustomHeadTableCell>{translate('fleetSenseApp.inspection.inspectionItemName')}</CustomHeadTableCell>
              <CustomHeadTableCell>{translate('fleetSenseApp.inspection.isCompliant')}</CustomHeadTableCell>
              <CustomHeadTableCell>{translate('fleetSenseApp.inspection.justification')}</CustomHeadTableCell>
              <CustomHeadTableCell>{translate('fleetSenseApp.inspection.image')}</CustomHeadTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {inspectionItemPerformeds?.map((item, index) => (
              <TableRow key={item.id}>
                <CustomBodyTableCell>{item.inspectionItem.name}</CustomBodyTableCell>
                <CustomBodyTableCell sx={{ p: 0 }}>
                  <FormControlLabel
                    label={<Typography fontSize="0.875rem">Sim</Typography>}
                    labelPlacement="start"
                    disabled
                    control={
                      <Checkbox
                        checked={item.satisfied === true}
                        icon={<RadioButtonUncheckedRounded />}
                        checkedIcon={<CheckCircleRounded />}
                        sx={{
                          '&.Mui-checked': {
                            color: theme.palette.success.main,
                          },
                        }}
                      />
                    }
                  />
                  <FormControlLabel
                    label={<Typography fontSize="0.875rem">Não</Typography>}
                    labelPlacement="start"
                    disabled
                    control={
                      <Checkbox
                        checked={item.satisfied === false}
                        icon={<RadioButtonUncheckedRounded />}
                        checkedIcon={<UnpublishedRounded />}
                        sx={{
                          '&.Mui-checked': {
                            color: theme.palette.error.main,
                          },
                        }}
                      />
                    }
                  />
                </CustomBodyTableCell>
                <CustomBodyTableCell>
                  <FormControlDefault variant="outlined">
                    <TextField
                      value={item.observations || ''}
                      disabled
                      multiline
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          padding: '4px 10px',
                          minWidth: 350,
                        },
                      }}
                      inputProps={{
                        style: { height: 50, overflow: 'auto', padding: 0 },
                      }}
                    />
                  </FormControlDefault>
                </CustomBodyTableCell>
                <CustomBodyTableCell>
                  <InspectionImageInput
                    disabled
                    description={item.observations}
                    image={item.image}
                    handleInputChange={null}
                    imageContentType="image/png"
                    index={index}
                  />
                </CustomBodyTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    ) : (
      <Box>
        <Typography
          sx={{
            whiteSpace: 'nowrap',
            color: 'text.primary',
            fontSize: '0.875rem',
            fontWeight: '600',
            mb: 1,
          }}
        >
          {translate('fleetSenseApp.inspectionHistory.observations')}
        </Typography>
        <TextField
          multiline
          placeholder={translate('fleetSenseApp.inspectionItem.observations')}
          value={observations}
          disabled
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': {
              padding: '20px',
            },
          }}
        />
      </Box>
    );
  };

  const CustomTableRow = ({ key, inspectionHistory }: CustomTableRowProps) => {
    return (
      <React.Fragment key={key}>
        <TableRow
          sx={{
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            backgroundColor: 'background.paper',
            filter: open && expandedId === inspectionHistory.id ? `drop-shadow(0 2px 2px ${theme.palette.grey[50]})` : 'none',
            borderTop: '4px solid',
            borderBottom: '4px solid',
            borderColor: 'primary.main',
          }}
          onClick={() => [
            (inspectionHistory.id !== expandedId || !open) &&
              inspectionHistory.inspection.type !== InspectionTypes.MAINTENANCE &&
              getinspectionItemPerformeds(inspectionHistory),
            setOpen(inspectionHistory.id !== expandedId || !open),
            setExpandedId(inspectionHistory.id),
          ]}
        >
          <CustomHeadTableCelll>
            {inspectionHistory.inspection.name}
            <IconButton color="secondary" component={Link} to={`inspection?inspectionId=${encodeURIComponent(inspectionHistory.inspection.id)}`}>
              <OpenInNewRounded fontSize="small" />
            </IconButton>
          </CustomHeadTableCelll>
          <CustomHeadTableCelll>
            {inspectionHistory.truckImplement.licensePlate}
            <IconButton color="secondary" component={Link} to={`dashboards/truck-implement/${inspectionHistory.truckImplement.id}`}>
              <OpenInNewRounded fontSize="small" />
            </IconButton>
          </CustomHeadTableCelll>
          <CustomHeadTableCelll>
            {inspectionHistory.user.firstName}
            <IconButton color="secondary" component={Link} to={`admin/user-management?emailSearch=${encodeURIComponent(inspectionHistory.user.email)}`}>
              <OpenInNewRounded fontSize="small" />
            </IconButton>
          </CustomHeadTableCelll>
          <CustomHeadTableCelll>
            {inspectionHistory.created ? <TextFormat type="date" value={inspectionHistory.created} format={APP_DATE_FORMAT} /> : null}
          </CustomHeadTableCelll>
          <CustomHeadTableCelll>{statusChip(inspectionHistory)}</CustomHeadTableCelll>
          <CustomHeadTableCelll>
            <IconButton aria-label="expand row" size="small" color="secondary">
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </CustomHeadTableCelll>
        </TableRow>
        <TableRow
          sx={{
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            borderTop: '4px solid',
            borderBottom: '4px solid',
            borderColor: 'primary.main',
          }}
        >
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
            <Collapse timeout="auto" unmountOnExit in={open && expandedId === inspectionHistory.id}>
              <Box mb={5} mt={2} p={2} pt={0}>
                {summaryDetails(inspectionHistory)}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  const searchTypes: ISearchTypes[] = [
    {
      label: translate('fleetSenseApp.inspectionHistory.searchLabel'),
      placeholder: translate('fleetSenseApp.inspectionHistory.searchPlaceholder'),
      value: setNameSearchPageHeader(),
      setValue: inspectionHistoryId ? setInspectionHistoryId : setNameSearch,
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ backgroundColor: 'primary.main', borderRadius: '16px', padding: '24px' }}>
        <PageHeader
          headingId="fleetSenseApp.inspectionHistory.home.title"
          headingDataCy="InspectionHistoryHeading"
          contentKey="fleetSenseApp.inspectionHistory.home.title"
          buttonTextKey="fleetSenseApp.inspectionHistory.home.createLabel"
          match={match}
          showButton={false}
        />
        <PageFilters searchTypes={searchTypes} setActivePage={setActivePage} showFilters={['filters']} />

        {!loading && !!inspectionHistoryList.length && (
          <TableContainer sx={{ mt: 1 }}>
            <Table aria-label="inspection history table">
              <TableHead>
                <TableRow>
                  {headers.map((header, i) => (
                    <TableCell
                      key={`entity-${i}`}
                      sx={{
                        borderBottom: 'none',
                        paddingBottom: 1,
                      }}
                    >
                      {header.sortable ? (
                        <TableSortLabel
                          active={paginationState.sort === header.key}
                          direction={paginationState.order as 'desc' | 'asc'}
                          onClick={sort(header.key)}
                          IconComponent={ArrowDownwardRounded}
                        >
                          <Typography
                            sx={{
                              color: 'text.primary',
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                            }}
                          >
                            {translate(header.component)}
                          </Typography>
                        </TableSortLabel>
                      ) : (
                        <Typography
                          sx={{
                            color: 'text.primary',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            alignItems: 'center',
                          }}
                        >
                          {translate(header.component)}
                        </Typography>
                      )}
                    </TableCell>
                  ))}
                  <TableCell sx={{ borderBottom: '0' }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {inspectionHistoryList?.map((inspectionHistory, i) => (
                  <CustomTableRow key={i} inspectionHistory={inspectionHistory} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {!loading && !inspectionHistoryList.length && <NoData title="Nenhum histórico de inspeção encontrado" />}
        {loading && <Loading />}
        <PaginationComponent paginationState={paginationState} totalItems={totalItems} onChange={handlePagination} list={inspectionHistoryList} />
      </Box>
    </ThemeProvider>
  );
};

export default InspectionHistory;

const CustomHeadTableCell = styled(TableCell)(({ theme }) => ({
  whiteSpace: 'nowrap',
  color: theme.palette.text.primary,
  fontSize: '0.875rem',
  fontWeight: '600',
  borderBottom: 'none',
  paddingBottom: 1,
  cursor: 'default',
  minWidth: 200,
  textAlign: 'center',
}));

const CustomBodyTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderBottom: '2px solid',
  borderTop: '2px solid',
  padding: '7px 0',
  borderColor: theme.palette.primary.main,
  cursor: 'default',
  color: theme.palette.primary.contrastText,
  textAlign: 'center',
}));

const CustomHeadTableCelll = styled(TableCell)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
}));
